import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import 'url-search-params-polyfill';
import '../App.css';
import logo from '../assets/img/vucity-logo.png';

import ErrorMessage from '../components/ErrorMessage';
import FrameSession from '../components/FrameSession';
import LoginForm from '../components/Login';
import Rating from '../components/Rating';
import Slideshow from '../components/Slideshow/Slideshow';
import SpinnerMessage from '../components/SpinnerMessage';
import TechnicalIssue from '../components/TechnicalError';
import * as consts from '../constants';
import getFrameToken from '../getFrameToken';

import {
  Eighth,
  Fifth,
  First,
  Fourth,
  Second,
  Seventh,
  Sixth,
  Third,
} from '../components/exportImages';
import { Header, Link, Logo } from '../components/UI';

class Benchmark extends Component {
  constructor() {
    super();

    this.state = {
      mode: consts.modes.spinner,
      status: 'Getting ready...',
      email: '',
      userToken: '',
      loginMethod: '',
      applications: {},
      selection: null,
      backend: null,
      hubRedirectUrl: null,
      frameToken: null,
    };

    // frame-react-app project on mixpanel
    mixpanel.init('9611c12e572e3b55217bbaaccffae320', {
      disable_persistence: true, // We don't request cookie perms, so disable peristant storage
    });
  }

  async componentDidMount() {
    // UNCOMMENT THIS FUNCTION CALL IF APP HAS TECHNICAL ISSUES
    // It will show a temporary message to users until problem is fixed
    // Can be used later to determine app status and automatically run it
    // this.isTechnicalIssue();

    // Grab the URL parameters
    const url = new URL(document.URL);
    const params = new URLSearchParams(url.search);

    // Read the backend from the referer
    // start with protocol://subdomain.vu(dev|stage|).city
    // '^\w+:\/\/': The string starts with a protocol e.g. 'https://'
    // '(?:\w+\.)*': Matches any number of subdomains without capturing e.g. 'any.thing.like.this.'
    // '(vu(?:dev|stage)?)': Captures vu, vudev, or vustage
    // '\.city': Matches our TLD
    // '(?:$|\/|\?)': Ensures that '.city' is the TLD
    const regex = /^\w+:\/\/(?:\w+\.)*(vu(?:dev|stage)?)\.city(?:$|\/|\?)/;
    const matches = regex.exec(document.referrer);
    if (matches != null) {
      this.setState({ backend: matches[1] });
    }

    // The dashboard will send us this so we can correctly attribute the
    // end-of-session ratings.
    // The email is also our identifier for MixPanel, so get it as early as
    // possible.
    const user = params.get('email');
    if (user) {
      const decodedEmail = decodeURIComponent(user);
      this.setState({ email: decodedEmail });
      mixpanel.identify(decodedEmail);
    } else {
      // Each unknown user counts as a unique user in Mixpanel which costs us
      // extra, so until we get the email log all analytics under the "unknown"
      // user.
      mixpanel.identify('unknown');
    }

    // Request the list of applications from the microservice
    const { accountID } = this.props;
    await this.loadApplications(accountID, true);

    // HACK: For the benchmark, we'll use the beta app
    const { applications } = this.state;
    applications[consts.betaApp.id] = consts.betaApp;
    this.setState({
      applications,
      selection: consts.betaApp.id,
    });

    // Allow setting the backend from the parameters as well as the referal URL
    const backend = params.get('backend');
    if (backend) {
      this.setState({ backend });
    }

    // Show the login box
    this.setState({ mode: consts.modes.login });
  }

  loadApplications = async (accountID, updateSelection) => {
    // Time how long this takes
    mixpanel.time_event(consts.eventNames.loadApps);

    const options = {
      method: 'POST',
      headers: {
        Authorization: process.env.REACT_APP_APP_LIST_SERVICE_AUTH,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ account_id: accountID }),
    };

    const response = await fetch(
      process.env.REACT_APP_APP_LIST_SERVICE_URL,
      options,
    );
    if (response.status === 200) {
      const payload = await response.json();

      // Append to the existing hash of applications
      this.setState((prevState) => ({
        applications: { ...prevState.applications, ...payload.applications },
      }));

      // Optionally update the selection state
      if (updateSelection) {
        this.setState({ selection: payload.dashboard_id });
      }
    }

    // Analytics
    mixpanel.track(consts.eventNames.loadApps, {
      success: response.status === 200,
      status: response.status,
      account: consts.accounts[accountID].name,
    });
  };

  handleSelectionChange = (event) => {
    this.setState({ selection: event.target.value });
  };

  handleSuccessfulLogin = (email, ident) => {
    this.showSpinner(consts.loggedIn);
    this.setState({
      loginMethod: 'dashboard',
      userToken: ident,
      email,
    });

    this.retrieveTokenAndStartFrameSession();
  }

  setMode = (mode, status) => {
    this.setState({ mode, status });
  }

  setupUserData = () => {
    const {
      email,
      userToken,
      loginMethod,
      backend,
      hubRedirectUrl,
    } = this.state;

    let userData = '{ "error": "Missing Auth Token" }';
    if (userToken) {
      const user = loginMethod === 'auth0' ? hubRedirectUrl : email;

      // App feature flags
      const features = ['BenchmarkTool', 'Analytics'];

      const payload = {
        user,
        token: userToken,
        method: loginMethod,
        features,
        backend,
        // Hardcoded to enter the london license
        license: 'London',
      };
      userData = JSON.stringify(payload);
    }
    return userData;
  }

  retrieveTokenAndStartFrameSession = async () => {
    // Time token retrieval
    mixpanel.time_event(consts.eventNames.getToken);

    let frameToken = null;
    let message = '';
    try {
      const { email } = this.state;
      frameToken = await getFrameToken(email);
      // The FrameSession component will handle the rest
      this.setState({ mode: consts.modes.session, frameToken });
    } catch (err) {
      this.showError(`Couldn't authorise cloud session: ${err.message}`);
      message = err.message;
    }

    // Analytics
    mixpanel.track(consts.eventNames.getToken, {
      success: frameToken != null,
      message,
    });
    message = '';
  }

  isTechnicalIssue = () => {
    this.setState({ mode: consts.modes.blocked });
  }

  showSpinner = (message) => {
    this.setState({
      mode: consts.modes.spinner,
      status: message,
    });
  }

  showError = (message) => {
    this.setState({
      mode: consts.modes.error,
      status: message,
    });
  }

  render() {
    const {
      status,
      mode,
      email,
      applications,
      selection,
      backend,
      frameToken,
    } = this.state;

    let launchpadId = '';
    if (applications && selection) {
      launchpadId = consts.accounts[applications[selection].accountID].launchpad;
    }

    return (
      <div className="App">
        <div className="slide-show">
          <Slideshow slides={[First, Second, Third, Fourth, Fifth, Sixth, Seventh, Eighth]} />
        </div>
        <div className="content-area">
          <div className="app-header">
            <Logo url={null} imgSrc={logo} />
            <Header size="3">
              Welcome to VU.CITY Cloud
            </Header>
          </div>

          {
            {
              error: (<ErrorMessage message={status} />),

              login: (
                <LoginForm
                  onLogin={this.handleSuccessfulLogin}
                  onSelectionChange={this.handleSelectionChange}
                  selection={selection}
                  applications={applications}
                  backend={backend}
                />
              ),

              spinner: (<SpinnerMessage message={status} />),

              session: (
                <FrameSession
                  authToken={frameToken}
                  launchpadId={launchpadId}
                  appId={selection}
                  userData={this.setupUserData()}
                  setMode={this.setMode}
                />
              ),

              rating: (
                /* TODO: Extract email of login based on ident */
                <Rating
                  email={email || ''}
                  setMode={this.setMode}
                />
              ),

              blocked: (
                <TechnicalIssue>
                  VU.CITY Cloud is currently experiencing technical issues.{' '}
                  <br />
                  We are working to fix these as quickly as we can. <br />
                  Service will resume as soon as possible.
                </TechnicalIssue>
              ),

              loginDisallowed: (
                <TechnicalIssue>
                  It is no longer possible to login via this page.<br />
                  Please visit <Link href="https://dashboard.vu.city/dashboard/">the dashboard</Link> to access VU.CITY Cloud.
                </TechnicalIssue>
              ),
            }[mode]
          }
        </div>
      </div>
    );
  }
}

Benchmark.propTypes = {
  accountID: PropTypes.string.isRequired,
};

export default Benchmark;
